import { useState } from "react";
import { PayoutAdjustment } from "./types";
import { usd } from "./helpers";

interface AdjustmentsSectionProps {
  adjustments: PayoutAdjustment[];
  totalAdjustments: number;
  onAdd: (description: string, amountCents: number) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

export const AdjustmentsSection = ({
  adjustments,
  totalAdjustments,
  onAdd,
  onDelete,
}: AdjustmentsSectionProps) => {
  const [showForm, setShowForm] = useState(false);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!description.trim() || !amount) return;
    const cents = Math.round(parseFloat(amount) * 100);
    if (cents <= 0) return;

    setSaving(true);
    try {
      await onAdd(description.trim(), cents);
      setDescription("");
      setAmount("");
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this adjustment?")) return;
    await onDelete(id);
  };

  return (
    <div className="space-y-2">
      {adjustments.length === 0 && !showForm && (
        <p className="text-sm text-gray-400 py-1">No adjustments.</p>
      )}

      {adjustments.map((adj) => (
        <div
          key={adj.id}
          className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded text-sm gap-2 min-w-0"
        >
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            <span className="text-gray-800 font-medium truncate">{adj.description || "Adjustment"}</span>
            <span className="text-xs text-gray-400">
              {adj.created_at
                ? new Date(adj.created_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                : ""}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="tabular-nums font-medium text-gray-900">
              {usd(adj.amount)}
            </span>
            <button
              onClick={() => handleDelete(adj.id)}
              className="text-red-500 hover:text-red-700 transition-colors"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      ))}

      {showForm && (
        <div className="space-y-2 py-1.5">
          <input
            type="text"
            placeholder="Description (e.g. Deposit return)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder="Amount ($)"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-28 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
            />
            <button
              onClick={handleSubmit}
              disabled={saving || !description.trim() || !amount}
              className="px-3 py-1.5 text-sm font-medium rounded-md bg-red-700 text-white hover:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => {
                setShowForm(false);
                setDescription("");
                setAmount("");
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-gray-300">
        <div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="text-sm font-medium text-red-700 hover:text-red-800 transition-colors flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Add Adjustment
            </button>
          )}
        </div>
        <span className="text-sm font-semibold text-gray-900 tabular-nums">
          Total: {usd(totalAdjustments)}
        </span>
      </div>
    </div>
  );
};
